import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export interface RecaptchaConfig {
  siteKey: string;
  enabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private apiUrl = environment.apiUrl;
  private configSubject = new BehaviorSubject<RecaptchaConfig | null>(null);
  public config$ = this.configSubject.asObservable();

  private scriptLoaded = false;
  private scriptLoadPromise: Promise<void> | null = null;

  constructor(private http: HttpClient, private ngZone: NgZone) {
    this.loadConfig();
  }

  private loadConfig(): void {
    this.http
      .get<{ status: string; siteKey: string; enabled: boolean }>(
        `${this.apiUrl}/auth/recaptcha-config`
      )
      .subscribe({
        next: (response) => {
          this.configSubject.next({
            siteKey: response.siteKey,
            enabled: response.enabled,
          });
        },
        error: (error) => {
          console.warn(
            'reCAPTCHA backend config failed, using environment:',
            error
          );
          if (environment.recaptchaSiteKey) {
            this.configSubject.next({
              siteKey: environment.recaptchaSiteKey,
              enabled: true,
            });
          } else {
            console.error('reCAPTCHA not configured in environment either');
            this.configSubject.next({
              siteKey: '',
              enabled: false,
            });
          }
        },
      });
  }

  async loadScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window object not available'));
        return;
      }

      if (window.grecaptcha) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const checkGrecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.render) {
          this.scriptLoaded = true;
          resolve();
        } else {
          setTimeout(checkGrecaptcha, 100);
        }
      };

      checkGrecaptcha();

      setTimeout(() => {
        if (!this.scriptLoaded) {
          reject(new Error('reCAPTCHA script failed to load'));
        }
      }, 15000);
    });

    return this.scriptLoadPromise;
  }

  async render(
    elementId: string,
    siteKey: string,
    callback?: (token: string) => void
  ): Promise<number> {
    await this.loadScript();

    return new Promise((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        try {
          const element = document.getElementById(elementId);
          if (!element) {
            reject(new Error(`Element with ID ${elementId} not found`));
            return;
          }

          const widgetId = window.grecaptcha.render(element, {
            sitekey: siteKey,
            callback: (token: string) => {
              this.ngZone.run(() => {
                if (callback) callback(token);
              });
            },
            'expired-callback': () => {
              this.ngZone.run(() => {
                if (callback) callback('');
              });
            },
            'error-callback': () => {
              this.ngZone.run(() => {
                if (callback) callback('');
              });
            },
          });
          resolve(widgetId);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  reset(widgetId?: number): void {
    if (window.grecaptcha && window.grecaptcha.reset) {
      if (widgetId !== undefined) {
        window.grecaptcha.reset(widgetId);
      } else {
        window.grecaptcha.reset();
      }
    }
  }

  getResponse(widgetId?: number): string {
    if (window.grecaptcha && window.grecaptcha.getResponse) {
      return window.grecaptcha.getResponse(widgetId);
    }
    return '';
  }

  isEnabled(): Observable<boolean> {
    return new Observable((observer) => {
      this.config$.subscribe((config) => {
        observer.next(config?.enabled || false);
      });
    });
  }

  getSiteKey(): Observable<string> {
    return new Observable((observer) => {
      this.config$.subscribe((config) => {
        observer.next(config?.siteKey || '');
      });
    });
  }
}
